import { useState, useEffect } from 'react';
import api from '@/lib/api';

/**
 * Řízený D-SI číselník pro výběr DCC jednotky laboratoře.
 *
 * Autoritativní seznam VALIDNÍCH jednotek je na backendu — `GET /api/dsi-units`
 * (`app/domain/dsi_units.py`). Tady řešíme jen PREZENTACI: token → čitelný popisek.
 * Neznámý token (kdyby backend přidal nový) se zobrazí surově → žádné rozbití.
 */
const DSI_LABELS = {
  '\\degreecelsius': 'stupeň Celsia (°C)',
  '\\kelvin': 'kelvin (K)',
  '\\pascal': 'pascal (Pa)',
  '\\hecto\\pascal': 'hektopascal (hPa)',
  '\\kilo\\pascal': 'kilopascal (kPa)',
  '\\mega\\pascal': 'megapascal (MPa)',
  '\\bar': 'bar',
  '\\milli\\bar': 'milibar (mbar)',
  '\\percent': 'procento (%)',
  '\\metre': 'metr (m)',
  '\\milli\\metre': 'milimetr (mm)',
  '\\centi\\metre': 'centimetr (cm)',
  '\\kilo\\metre': 'kilometr (km)',
  '\\metre\\per\\second': 'metr za sekundu (m/s)',
  '\\degree': 'stupeň (°)',
  '\\gram': 'gram (g)',
  '\\kilogram': 'kilogram (kg)',
  '\\milli\\gram': 'miligram (mg)',
  '\\second': 'sekunda (s)',
  '\\hertz': 'hertz (Hz)',
  '\\litre': 'litr (l)',
  '\\milli\\litre': 'mililitr (ml)',
  '\\ampere': 'ampér (A)',
  '\\volt': 'volt (V)',
  '\\ohm': 'ohm (Ω)',
  '\\newton': 'newton (N)',
  '\\joule': 'joule (J)',
  '\\watt': 'watt (W)',
  '\\lux': 'lux (lx)',
  '\\candela': 'kandela (cd)',
  '\\radian': 'radián (rad)',
  '\\metre\\tothe{2}': 'metr čtvereční (m²)',
  '\\metre\\tothe{3}': 'metr krychlový (m³)',
  '\\kilogram\\per\\metre\\tothe{3}': 'kilogram na metr krychlový (kg/m³)',
};

// Fallback pro případ, že GET /api/dsi-units selže — zrcadlí backend VALID_DSI_UNITS.
const DSI_UNITS_FALLBACK = Object.keys(DSI_LABELS);

export function dsiLabel(symbol) {
  return DSI_LABELS[symbol] || symbol;
}

let _cache = null;
let _inflight = null;

function _load() {
  if (_cache) return Promise.resolve(_cache);
  if (!_inflight) {
    _inflight = api
      .get('/dsi-units')
      .then((r) => {
        _cache = Array.isArray(r.data) && r.data.length ? r.data : DSI_UNITS_FALLBACK;
        return _cache;
      })
      .catch(() => {
        _inflight = null; // umožní retry při příštím mountu
        // eslint-disable-next-line no-console
        console.warn('GET /api/dsi-units selhal — fallback na FE konstantu DSI_UNITS_FALLBACK');
        return DSI_UNITS_FALLBACK;
      });
  }
  return _inflight;
}

/**
 * Vrací D-SI jednotky jako `[{ symbol, label }]` pro dropdown (symbol = D-SI token,
 * label = čitelný popisek). Primárně z API, fallback na konstantu.
 */
export function useDsiUnits() {
  const [units, setUnits] = useState(() =>
    (_cache || []).map((symbol) => ({ symbol, label: dsiLabel(symbol) }))
  );
  useEffect(() => {
    let active = true;
    _load().then((tokens) => {
      if (active) setUnits(tokens.map((symbol) => ({ symbol, label: dsiLabel(symbol) })));
    });
    return () => {
      active = false;
    };
  }, []);
  return units;
}
